// src/pages/platform/PlatformProductsPage.tsx
// Platform products catalog — grid of product cards with status filters.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterTab = 'all' | 'enabled' | 'disabled' | 'beta';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map PlatformProductStatus to Badge variant. */
function statusVariant(
  status: string,
): 'success' | 'secondary' | 'default' | 'warning' | 'error' {
  switch (status) {
    case 'PLATFORM_PRODUCT_STATUS_ENABLED':
      return 'success';
    case 'PLATFORM_PRODUCT_STATUS_DISABLED':
      return 'secondary';
    case 'PLATFORM_PRODUCT_STATUS_COMING_SOON':
      return 'default';
    case 'PLATFORM_PRODUCT_STATUS_BETA':
      return 'warning';
    case 'PLATFORM_PRODUCT_STATUS_DEPRECATED':
      return 'error';
    default:
      return 'secondary';
  }
}

/** Human-readable status label. */
function formatStatus(status: string): string {
  switch (status) {
    case 'PLATFORM_PRODUCT_STATUS_ENABLED':
      return 'Enabled';
    case 'PLATFORM_PRODUCT_STATUS_DISABLED':
      return 'Disabled';
    case 'PLATFORM_PRODUCT_STATUS_COMING_SOON':
      return 'Coming Soon';
    case 'PLATFORM_PRODUCT_STATUS_BETA':
      return 'Beta';
    case 'PLATFORM_PRODUCT_STATUS_DEPRECATED':
      return 'Deprecated';
    default:
      return status.replace('PLATFORM_PRODUCT_STATUS_', '').replace(/_/g, ' ');
  }
}

/** Check whether a product matches the active filter tab. */
function matchesFilter(product: any, tab: FilterTab): boolean {
  if (tab === 'all') return true;
  const status: string = product.status ?? '';
  switch (tab) {
    case 'enabled':
      return status === 'PLATFORM_PRODUCT_STATUS_ENABLED';
    case 'disabled':
      return status === 'PLATFORM_PRODUCT_STATUS_DISABLED';
    case 'beta':
      return status === 'PLATFORM_PRODUCT_STATUS_BETA';
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Star icon for featured products
// ---------------------------------------------------------------------------

function StarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * PlatformProductsPage - displays all products registered on a platform.
 * Route: /platforms/:id/products
 */
export const PlatformProductsPage = observer(() => {
  const { id: platformId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = usePlatform();
  const { t } = useI18n();

  const [products, setProducts] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Fetch platform details (for breadcrumb name) and products list.
  useEffect(() => {
    if (!platformId) return;
    store.GetPlatform(platformId);
    store.ListPlatformProducts(platformId).then((res: any) => {
      setProducts(res?.products?.items ?? []);
    }).catch(() => {});

    return () => {
      store.clearCurrent();
    };
  }, [platformId]);

  const platformName = (store.currentItem as any)?.name ?? t('platform.details');

  /** Toggle enable / disable for a product. */
  const toggleProduct = async (product: any) => {
    if (!platformId) return;
    const productId = product.entity?.id;
    if (!productId) return;

    setTogglingId(productId);
    try {
      if (product.status === 'PLATFORM_PRODUCT_STATUS_ENABLED') {
        await store.DisablePlatformProduct(platformId, productId, {});
      } else {
        await store.EnablePlatformProduct(platformId, productId, {});
      }
      // Refresh list
      const res: any = await store.ListPlatformProducts(platformId);
      setProducts(res?.products?.items ?? []);
    } catch {
      // Error handled by store
    } finally {
      setTogglingId(null);
    }
  };

  const filteredProducts = products.filter((p) => matchesFilter(p, activeTab));

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: t('common.all') },
    { key: 'enabled', label: t('platform.products.enabled') },
    { key: 'disabled', label: t('platform.products.disabled') },
    { key: 'beta', label: t('platform.products.beta') },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div>
        <nav className="text-sm">
          <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
            {t('platform.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link
            to={`/platforms/${platformId}`}
            className="text-muted-foreground hover:text-foreground"
          >
            {platformName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('platform.products.title')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('platform.products.title')}</h1>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {store.isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )}

      {/* Empty state */}
      {!store.isLoading && filteredProducts.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {t('platform.products.empty')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {activeTab !== 'all'
                ? t('platform.products.emptyFilter')
                : t('platform.products.emptyDescription')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Product grid */}
      {!store.isLoading && filteredProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product: any) => {
            const productId = product.entity?.id;
            const name =
              product.branding?.name_override || product.product?.id || productId || '';
            const tagline = product.branding?.tagline_override || '';
            const status = product.status ?? '';
            const isToggling = togglingId === productId;

            return (
              <Card
                key={productId}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() =>
                  navigate(`/platforms/${platformId}/products/${productId}`)
                }
              >
                <CardContent className="p-4">
                  {/* Top row: name, featured star, status badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-medium text-foreground truncate">{name}</h3>
                        {product.is_featured && (
                          <StarIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        )}
                      </div>
                      {tagline && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {tagline}
                        </p>
                      )}
                    </div>
                    <Badge variant={statusVariant(status)} size="sm" className="ml-2 flex-shrink-0">
                      {formatStatus(status)}
                    </Badge>
                  </div>

                  {/* Tenant count */}
                  <div className="text-sm text-muted-foreground">
                    {product.current_tenants ?? 0} / {product.max_tenants ? product.max_tenants : '\u221E'} tenants
                  </div>

                  {/* Actions */}
                  <div
                    className="flex gap-2 mt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Link to={`/platforms/${platformId}/products/${productId}`}>
                      <Button size="sm" variant="secondary">
                        {t('platform.products.configure')}
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isToggling}
                      onClick={() => toggleProduct(product)}
                    >
                      {isToggling
                        ? t('common.loading')
                        : status === 'PLATFORM_PRODUCT_STATUS_ENABLED'
                          ? t('platform.products.disable')
                          : t('platform.products.enable')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default PlatformProductsPage;
